import {ActivityAreaResource, CompanySuggestionsResource, ExperienceResource} from '@/common/api/api-raw-types';
import {urlService} from 'vue3-starter/dist/http/UrlService';
import {context} from 'vue3-starter/src/typescript/app-context/AppContext';
import {ActivityArea, CompanySuggestion, Experience} from '../../domain/experience/Experience';
import {ExperienceRepository} from '../../domain/experience/ExperienceRepository';
import {toExperienceResource} from '../mapper/request/UpdateExperiencesRequestMapper';
import {toActivityArea} from '../mapper/resource/ActivityAreaResourceMapper';
import {toCompanySuggestion} from '../mapper/resource/CompanySuggestionResourceMapper';
import {toExperience} from '../mapper/resource/ExperienceResourceMapper';

// TODO: This function should be moved to ExperienceDomainService once the completion store start using ExperienceDomainService
export function sortByDate(a: Experience, b: Experience): number {
    const endDateA = a.endDate && !a.isCurrentJob ? new Date(a.endDate) : null;
    const endDateB = b.endDate && !b.isCurrentJob ? new Date(b.endDate) : null;

    if (!endDateA && !endDateB && a.startDate && b.startDate) {
        return b.startDate.getTime() - a.startDate.getTime();
    } else if (!endDateA) {
        return -1;
    } else if (!endDateB) {
        return 1;
    }

    if ((endDateA.getMonth(), endDateA.getFullYear()) === (endDateB.getMonth(), endDateB.getFullYear())) {
        if (a.startDate && b.startDate) {
            return b.startDate?.getTime() - a.startDate?.getTime();
        }
    }

    return endDateB.getTime() - endDateA.getTime();
}

export class ExperienceRepositoryAPI implements ExperienceRepository {
    async fetchActivityAreas(): Promise<ActivityArea[]> {
        const [data, error] = await context.httpService.get<ActivityAreaResource[]>(context.registry.entries.allActivityAreas());
        if (error) {
            throw new Error(error.message);
        }

        return data!.map((area) => toActivityArea(area));
    }

    async fetchExperiences(): Promise<Experience[]> {
        const [data, error] = await context.httpService.get<ExperienceResource[]>(context.registry.entries.getExperiences());
        if (error) {
            throw new Error(error.message);
        }

        const experiences: Experience[] = [];
        if (data && data.length > 0) {
            data.forEach((experienceResource) => {
                experiences.push(toExperience(experienceResource));
            });
        }

        return experiences.sort(sortByDate);
    }

    async searchCompanies(query: string): Promise<CompanySuggestion[]> {
        const entry = `${urlService.buildUrl(context.registry.entries.suggestCompanies(), query)}`;

        const [data, error] = await context.httpService.get<CompanySuggestionsResource>(entry);
        if (error) {
            throw new Error(error.message);
        }

        const companySuggestions: CompanySuggestion[] = [];
        if (data && data.suggestions && data.suggestions.length > 0) {
            data.suggestions.forEach((companySuggestionResource) => {
                companySuggestions.push(toCompanySuggestion(companySuggestionResource));
            });
        }

        return companySuggestions;
    }

    async addExperience(experience: Experience): Promise<void> {
        const experienceRequest = toExperienceResource(experience);
        const [data, error] = await context.httpService.post(context.registry.entries.getExperiences(), experienceRequest);
        if (error) {
            throw new Error(error.message);
        }
    }

    async editExperience(experience: Experience): Promise<void> {
        const experienceRequest = toExperienceResource(experience);
        const url = urlService.buildUrl(context.registry.entries.editExperience(), experienceRequest.id);
        const [data, error] = await context.httpService.put(url, experienceRequest);
        if (error) {
            throw new Error(error.message);
        }
    }

    async deleteExperience(experienceId: string): Promise<void> {
        const url = urlService.buildUrl(context.registry.entries.editExperience(), experienceId);
        const [data, error] = await context.httpService.request({url, method: 'DELETE', params: {origin: 'SIGNUP'}});
        if (error) {
            throw new Error(error.message);
        }
    }
}
